import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchActionCheckins, fetchShift, saveActionCheckin, setShiftDay, fetchGoals } from '../../api/goalsApi';
import { notifyGoalsUpdated } from '../../utils/goalsLocalStore';
import { CHECKIN_BACKFILL_DAYS, CHECKIN_STRIP_DAYS } from '../../constants/checkinReasons';
import { addDays, dayLabel, tokyoToday, weekday } from '../../utils/tokyoDate';
import CheckinSheet from './CheckinSheet';
import '../../styles/adminApproval.css';

/**
 * 行動記録 strip (last 7 days; the sheet reaches back 1 month) — inside 目標 → 毎日の行動プラン.
 * Shows which of the last 7 days are recorded; 「記録を編集」 / tapping a day opens CheckinSheet.
 * `canEdit` false (someone else's 目標) → read-only strip.
 * `refreshKey` changes when today was recorded elsewhere on the page (home popup) → reload.
 * 2026-10-28: days off (shift) show 「休」 and are not counted as 未記録; the sheet can mark a day 休み／出勤.
 */
export default function CheckinWeekStrip({ store, stylist, actions, canEdit, refreshKey = 0 }) {
  const today = tokyoToday();
  // Strip = last 7 days; the sheet can go back a month (older days via its date field).
  // Past 7 days + next 7 days (owner 2026-10-28: scroll ahead to mark 休み／出勤). Scrolls so today is in view.
  const days = useMemo(
    () => Array.from({ length: CHECKIN_STRIP_DAYS * 2 }, (_, i) => addDays(today, i - (CHECKIN_STRIP_DAYS - 1))),
    [today]
  );
  const ahead = addDays(today, CHECKIN_STRIP_DAYS);
  const stripRef = useRef(null);
  useEffect(() => {
    const el = stripRef.current;
    const t = el?.querySelector('[data-today="1"]');
    if (el && t) el.scrollLeft = Math.max(0, t.offsetLeft - el.clientWidth + t.offsetWidth + 8);
  }, [days]);
  const oldest = addDays(today, -(CHECKIN_BACKFILL_DAYS - 1));
  const [recordByDay, setRecordByDay] = useState({});
  const [offByDay, setOffByDay] = useState({}); // {[ymd]: true} shift days off in the window
  const [openDay, setOpenDay] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!store || !stylist) return;
    try {
      const [res, shift] = await Promise.all([
        fetchActionCheckins({ store, stylist, start: oldest, end: today, limit: CHECKIN_BACKFILL_DAYS }),
        fetchShift({ store, stylist, start: oldest, end: ahead }).catch(() => null), // unknown → no 休 marks
      ]);
      const map = {};
      (res.checkins || []).forEach((c) => { if (c?.date) map[c.date] = c; });
      setRecordByDay(map);
      const off = {};
      (shift?.offDays || []).forEach((d) => { off[d] = true; });
      setOffByDay(off);
    } catch {
      // Strip stays empty (all 未記録) — the sheet still saves; don't block the 目標 screen on this.
    }
  }, [store, stylist, oldest, ahead, today]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  const missing = days.filter((d) => d < today && !recordByDay[d] && !offByDay[d]).length;
  const oldestMissing = days.find((d) => d < today && !recordByDay[d] && !offByDay[d]) || today;

  const onToggleOff = async (day, off) => {
    setSaving(true);
    setError(null);
    try {
      await setShiftDay({ store, stylist, date: day, off });
      setOffByDay((p) => ({ ...p, [day]: off }));
    } catch (err) {
      setError(err?.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const onSave = async (day, entries) => {
    setSaving(true);
    setError(null);
    try {
      const res = await saveActionCheckin({ store, stylist, date: day, entries, source: 'goals_past' });
      const doc = res?.checkin || { date: day, entries };
      setRecordByDay((p) => ({ ...p, [day]: doc }));
      // 「今月 N回」 moved on the server — refresh the plan counters.
      const g = await fetchGoals({ store, stylist }).catch(() => null);
      if (g?.goals) notifyGoalsUpdated(g.goals);
      setOpenDay(null);
    } catch (err) {
      setError(err?.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const icon = (d) => {
    const c = recordByDay[d];
    if (!c && offByDay[d]) return <span className="ck-ic ck-ic--off" aria-label="休み">休</span>;
    if (!c && d > today) return <span className="ck-ic ck-ic--work" aria-label="出勤">出</span>;
    if (!c) return <span className="ck-ic ck-ic--none" aria-label="未記録">○</span>;
    const all = (c.entries || []).every((e) => e.status === 'done');
    return all
      ? <span className="ck-ic ck-ic--done" aria-label="すべてできた">✓</span>
      : <span className="ck-ic ck-ic--part" aria-label="一部できなかった">◐</span>;
  };

  return (
    <div className="ck-wrap">
      <div className="ck-head">
        <span className="ck-title">
          行動記録（前後{CHECKIN_STRIP_DAYS}日）
          <span className="ck-missing">{missing ? ` · 未記録 ${missing}日` : ' · すべて記録済み'}</span>
        </span>
        {canEdit && (
          <span className="ck-head-btns">
            {!recordByDay[today] && (
              <button type="button" className="set-group-edit" disabled={saving} onClick={() => onToggleOff(today, !offByDay[today])}>
                {offByDay[today] ? '今日を出勤に戻す' : '今日は休み'}
              </button>
            )}
            <button type="button" className="set-group-edit" onClick={() => setOpenDay(oldestMissing)}>
              記録を編集
            </button>
          </span>
        )}
      </div>
      {error && !openDay && <div className="ck-err" role="alert">{error}</div>}
      <div className="ck-days" ref={stripRef}>
        {days.map((d) => (
          <button
            key={d}
            type="button"
            data-today={d === today ? '1' : undefined}
            className={`ck-day${openDay === d ? ' is-on' : ''}${d > today ? ' is-ahead' : ''}`}
            disabled={!canEdit}
            onClick={() => canEdit && setOpenDay(d)}
          >
            <span className="ck-wd">{d === today ? '今日' : weekday(d)}</span>
            {icon(d)}
            <span className="ck-wd">{dayLabel(d)}</span>
          </button>
        ))}
      </div>
      {openDay && (
        <CheckinSheet
          key={openDay}
          days={days}
          oldest={oldest}
          today={today}
          recordByDay={recordByDay}
          offByDay={offByDay}
          actions={actions}
          initialDay={openDay}
          saving={saving}
          error={error}
          onSave={onSave}
          onToggleOff={onToggleOff}
          onClose={() => { setOpenDay(null); setError(null); }}
        />
      )}
    </div>
  );
}
